import React from 'react';
import { useTranslation } from 'react-i18next';
import { AiOutlineLock } from 'react-icons/ai';
import { BsCheckCircle } from 'react-icons/bs';
import { QuizItemProps } from '../../interfaces/components/quizItem';

/**
 * QuizItem - Displays a quiz item with locked/completed states and click functionality
 *
 * @component
 * @example
 * ```tsx
 * <QuizItem
 *   title="Introduction Quiz"
 *   locked={false}
 *   completed={false}
 *   onPress={() => console.log('Quiz started')}
 * />
 * ```
 */
const QuizItem: React.FC<QuizItemProps> = ({ title, locked, completed, onPress }) => {
  const { t } = useTranslation();

  return (
    <div
      className={`flex items-center p-3 border-l-4 cursor-pointer transition-colors
        ${completed ? 'border-green-500 bg-green-50/30 dark:bg-green-900/20 hover:bg-green-50/40 dark:hover:bg-green-900/30' : 'border-transparent hover:bg-gray-50 dark:hover:bg-gray-800/50'}
        ${locked ? 'opacity-60 cursor-not-allowed border-transparent' : ''}`}
      onClick={locked ? undefined : onPress}
      role="button"
      aria-disabled={locked}
      aria-label={
        locked ? t('courseDetails.content.quizLocked', { title }) : t('courseDetails.content.startQuiz', { title })
      }
    >
      <div className="w-10 h-10 mr-3 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0 border border-gray-200 dark:border-gray-700">
        {locked ? (
          <AiOutlineLock size={16} className="text-gray-400 dark:text-gray-500" />
        ) : completed ? (
          <BsCheckCircle size={16} className="text-green-500" />
        ) : (
          <div className="text-gray-700 dark:text-gray-300 font-bold text-sm">Q</div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-medium text-titleText dark:text-gray-100 text-sm truncate">{title}</h3>
        <p className="text-xs text-gray-500 dark:text-gray-400">{t('courseDetails.content.assessment')}</p>
      </div>
    </div>
  );
};

export default QuizItem;
